import { useState } from "react";
import InfoTip from "@/shared/components/infoTip/InfoTip";
import TouchRow, { type TouchRowModel } from "./TouchRow";
import { blockFor, conflictsFor } from "./touchBlocks";
import AddStepForm, { type AddStepDraft } from "./AddStepForm";
import {
  CustomizedPopover,
  TouchRowMenu,
  diffOf,
  isEdited,
  type Anchor,
} from "./TouchActions";
import {
  anchorName,
  markPairs,
  sendDate,
  type KeyDateRef,
  type Sequence,
  type SequenceSettings,
  type Touch,
  type TriggerDirection,
} from "./emailSmsTypes";

const COLLAPSED_ROWS = 5;

type Props = {
  sequence: Sequence;
  settings: SequenceSettings;
  keyDates: KeyDateRef[];
  typeLabels?: Record<string, string>;
  campaignAudienceLabel: string;
  smsAudienceLabel: string;
  audienceOptions: { v: string; label: string }[];
  /** Whether the campaign itself has each channel switched on. */
  campaignEmailOn: boolean;
  campaignSmsOn: boolean;
  /** Merge values in force on this campaign, for the empty-brand-token block. */
  values: Record<string, string>;
  campaignStart?: string | null;
  campaignEnd?: string | null;
  onSettings: (next: Partial<SequenceSettings>) => void;
  onTouchChange: (touchId: string, next: Partial<Touch>) => void;
  onPreviewTouch: (touchId: string, mode?: "preview" | "edit") => void;
  onOpenCatalog: () => void;
  onManageAudiences: () => void;
  onFixBlock: (target: string) => void;
  onAddStep: (draft: AddStepDraft) => void;
  /** False until the catalog can create the message a step points at (MPN-2201). */
  canAddStep?: boolean;
  /** A real test send of this touch, to whoever is logged in (MPN-2206). */
  onSendTest: (touchId: string) => void;
  /** Move one touch's approval status by hand (MPN-2207). */
  onTouchStatus: (touchId: string, next: string) => void;
  /** Touch ids whose status change is still in flight. */
  statusBusy?: Set<string>;
  onResetTouch: (touchId: string) => void;
  onDeleteTouch: (touchId: string) => void;
};

export function SequenceCard({
  sequence,
  settings,
  keyDates,
  typeLabels,
  campaignAudienceLabel,
  smsAudienceLabel,
  audienceOptions,
  campaignEmailOn,
  campaignSmsOn,
  values,
  campaignStart,
  campaignEnd,
  onSettings,
  onTouchChange,
  onPreviewTouch,
  onOpenCatalog,
  onManageAudiences,
  onFixBlock,
  onAddStep,
  canAddStep = false,
  onSendTest,
  onTouchStatus,
  statusBusy,
  onResetTouch,
  onDeleteTouch,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [settingsShown, setSettingsShown] = useState(false);
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [menuAt, setMenuAt] = useState<{ id: string; at: Anchor } | null>(null);
  const [custAt, setCustAt] = useState<{ id: string; at: Anchor } | null>(null);

  const draft = !sequence.published;
  // From the playbook, not from `touches`: a switched-off channel has no touches here,
  // so neither the toggle nor the hidden-count can be derived from them.
  const hasEmail = sequence.emailCount > 0;
  const hasSms = sequence.smsCount > 0;

  // A channel the campaign has switched off hides its touches here rather than
  // showing rows that cannot send.
  const visible = sequence.touches.filter(
    (t) =>
      (t.ch === "email" ? settings.emailCh && campaignEmailOn : settings.smsCh && campaignSmsOn),
  );
  /*
      Why they are hidden, not just how many. There are TWO switches per channel — this
      playbook's, and the campaign's Email or Text Messages block — and the campaign's is
      the harder gate. Saying only "2 email touches hidden" beside a playbook switch that
      reads ON is a contradiction the reader cannot resolve, so each note names the switch
      that is actually off and offers a way to it.
  */
  const hiddenNotes: { text: string; toCampaign: boolean }[] = [];
  if (hasEmail && !(settings.emailCh && campaignEmailOn)) {
    hiddenNotes.push(
      campaignEmailOn
        ? { text: `${sequence.emailCount} email touches hidden — email is off for this playbook`,
            toCampaign: false }
        : { text: `${sequence.emailCount} email touches hidden — email is switched off on the campaign`,
            toCampaign: true },
    );
  }
  if (hasSms && !(settings.smsCh && campaignSmsOn)) {
    hiddenNotes.push(
      campaignSmsOn
        ? { text: `${sequence.smsCount} text touches hidden — text is off for this playbook`,
            toCampaign: false }
        : { text: `${sequence.smsCount} text touches hidden — text is switched off on the campaign`,
            toCampaign: true },
    );
  }

  const pairs = markPairs(visible);
  // Only live rows are judged: a touch in a channel this campaign has switched off is not
  // broken, it is simply not sending.
  const live = settings.on ? visible.filter((t) => t.on !== false) : [];
  const conflicts = conflictsFor(live, keyDates);
  const blockCtx = { keyDates, typeLabels, values, campaignStart, campaignEnd };
  // The catalog original behind each touch; absent for a step added on this campaign.
  const originalOf = (id: string) => sequence.touches.find((t) => t.id === id);
  const anchorOptions = keyDates.map((k) => ({
    v: k.type,
    label: k.label?.trim() || anchorName(k.type),
  }));

  /*
      The options a touch may be anchored to, plus the one it IS anchored to.

      A select whose value matches no option renders blank, and that is what every touch on a
      campaign missing its anchor's key date looked like: "3 days after" and then an empty
      box, with no way to tell what the touch was waiting for. The anchor is real whether or
      not the campaign carries that date -- the blocking banner under the row is what says it
      is missing -- so it is listed, and the row reads "3 days after Campaign Start".
  */
  const optionsFor = (anchor: string) =>
    anchorOptions.some((o) => o.v === anchor)
      ? anchorOptions
      : [...anchorOptions, { v: anchor, label: anchorName(anchor) }];

  const emailCount = visible.filter((t) => t.ch === "email").length;
  const smsCount = visible.filter((t) => t.ch === "sms").length;
  const summaryLine = [
    `${visible.length} touches`,
    emailCount ? `${emailCount} email` : "",
    smsCount ? `${smsCount} text` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const toModel = (touch: Touch, index: number): TouchRowModel => {
    const when = sendDate(touch, keyDates);
    const block = live.includes(touch) ? blockFor(touch, blockCtx) : null;
    return {
      ...touch,
      n: index + 1,
      customized: isEdited(diffOf(touch, originalOf(touch.id))),
      pair: pairs.get(touch.id),
      hasDays: touch.dir !== "on",
      hasToggle: true,
      toggleDisabled: draft,
      hasStop: touch.ch === "sms",
      anchorOptions: optionsFor(touch.anchor),
      dateLabel: when
        ? when.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
        : undefined,
      statusLabel: block ? "Blocked" : touch.on === false ? "Off" : "On",
      statusTone: block ? "blocked" : touch.on === false ? "off" : "on",
      custStale: diffOf(touch, originalOf(touch.id)).catalogUpdated,
      blockHead: block?.head,
      blockRest: block?.rest,
      blockFix: block?.fix,
      conflictRest: conflicts.get(touch.id),
    };
  };

  const groups = sequence.groups.length
    ? sequence.groups
    : [{ id: "main", name: "", note: "" }];

  return (
    <div className="esms-seq">
      <div className="esms-seq__head">
        <div className="esms-seq__main">
          <button
            type="button"
            className="esms-seq__name-btn"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <span className="esms-seq__name">{sequence.name}</span>
          </button>
          <div className="esms-seq__sub">
            <span>{summaryLine}</span>
            <button
              type="button"
              className="esms__link"
              aria-expanded={settingsShown}
              onClick={() => {
                setSettingsShown((v) => !v);
                setExpanded(true);
              }}
            >
              {settingsShown ? "Hide settings" : "Settings"}
            </button>
          </div>
        </div>

        <span className="esms-seq__controls">
          {draft && <span className="touch-pill touch-pill--off">Draft</span>}
          <button
            type="button"
            role="switch"
            aria-checked={settings.on}
            aria-disabled={draft}
            aria-label={`${settings.on ? "Turn off" : "Turn on"} ${sequence.name}`}
            className={`touch-switch${settings.on ? " touch-switch--on" : ""}`}
            title={
              draft
                ? "Only published sequences can be turned on. Publish it in the Email & SMS Catalog."
                : undefined
            }
            onClick={() => !draft && onSettings({ on: !settings.on })}
          >
            <span className="touch-switch__knob" />
          </button>
          <button
            type="button"
            className="esms-seq__chev"
            aria-expanded={expanded}
            aria-label="Show touches"
            onClick={() => setExpanded((v) => !v)}
          >
            <i className={expanded ? "bi bi-chevron-up" : "bi bi-chevron-down"} />
          </button>
        </span>
      </div>

      {expanded && (
        <div className="esms-seq__body">
          {settingsShown && (
            <div className="esms-seq__settings">
              <div className="esms-seq__channels">
                <span className="esms-seq__channels-label">Channels</span>
                {hasEmail && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.emailCh && campaignEmailOn}
                    className="esms-seq__ch-toggle"
                    // The campaign's Email block wins. Leaving this switch usable would let
                    // somebody turn email "on" here and watch nothing change.
                    disabled={!campaignEmailOn}
                    aria-describedby={campaignEmailOn ? undefined : `${sequence.id}-email-off`}
                    onClick={() => onSettings({ emailCh: !settings.emailCh })}
                  >
                    <span
                      className={`touch-switch${
                        settings.emailCh && campaignEmailOn ? " touch-switch--on" : ""
                      }`}
                    >
                      <span className="touch-switch__knob" />
                    </span>
                    Email
                  </button>
                )}
                {hasSms && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.smsCh && campaignSmsOn}
                    className="esms-seq__ch-toggle"
                    disabled={!campaignSmsOn}
                    onClick={() => onSettings({ smsCh: !settings.smsCh })}
                  >
                    <span
                      className={`touch-switch${
                        settings.smsCh && campaignSmsOn ? " touch-switch--on" : ""
                      }`}
                    >
                      <span className="touch-switch__knob" />
                    </span>
                    SMS
                  </button>
                )}
              </div>

              <div className="esms-seq__facts">
                <span className="esms-seq__fact-key">Target action</span>
                <span className="esms-seq__fact-val esms-seq__fact-val--strong">
                  {sequence.target}
                </span>
                <span className="esms-seq__fact-key">Window</span>
                <span className="esms-seq__fact-val">{sequence.win}</span>

                {hasEmail && settings.emailCh && (
                  <>
                    <span className="esms-seq__fact-key">
                      Email audience
                      <InfoTip
                        label="Exit rule"
                        text="Students leave when they complete the target action or no longer match the audience."
                      />
                    </span>
                    <div className="esms-seq__sub">
                      <select
                        aria-label="Email audience mode"
                        value={settings.audMode}
                        onChange={(e) =>
                          onSettings({ audMode: e.target.value as SequenceSettings["audMode"] })
                        }
                      >
                        <option value="campaign">Same as the campaign audience</option>
                        <option value="separate">A separate audience</option>
                      </select>
                      {settings.audMode === "campaign" ? (
                        <span className="esms-seq__fact-val--nums">{campaignAudienceLabel}</span>
                      ) : (
                        <>
                          <select
                            aria-label="Audience"
                            value={settings.audPick}
                            onChange={(e) => onSettings({ audPick: e.target.value })}
                          >
                            {audienceOptions.map((o) => (
                              <option key={o.v} value={o.v}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <button type="button" className="esms__link" onClick={onManageAudiences}>
                            Manage audiences
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}

                {hasSms && settings.smsCh && (
                  <>
                    <span className="esms-seq__fact-key">
                      SMS audience
                      <InfoTip
                        label="SMS audience"
                        text="Only people with a clear yes in the school's consent column. Opt-outs stay excluded."
                      />
                    </span>
                    <span className="esms-seq__fact-val esms-seq__fact-val--nums">
                      {smsAudienceLabel}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {draft && (
            <div className="esms-seq__draft">
              <span>
                <strong>Draft in the catalog.</strong> Only published sequences can be turned on.
              </span>
              <button type="button" className="esms__link" onClick={onOpenCatalog}>
                Open in catalog
              </button>
            </div>
          )}

          {hiddenNotes.length > 0 && (
            <div className="esms-seq__sub">
              {hiddenNotes.map((note) => (
                <span key={note.text} id={`${sequence.id}-email-off`}>
                  <i className="bi bi-eye-slash" /> {note.text}
                  {note.toCampaign && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="esms-seq__inline-link"
                        onClick={() => onFixBlock("campaign")}
                      >
                        Open the campaign
                      </button>
                    </>
                  )}
                </span>
              ))}
            </div>
          )}

          <div className="esms-seq__toolbar">
            <span className="esms__spacer" />
            {/*
                Disabled rather than silently doing nothing. A step needs a message in the
                catalog to point at, and the catalog has no write path yet (MPN-2201). The
                form and its trigger rules are built and wired; only the final save is
                missing, so this comes back by dropping `canAddStep`.
            */}
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={!canAddStep || addOpen || draft}
              title={
                canAddStep
                  ? undefined
                  : "Adding a step needs the message catalog, which isn't built yet."
              }
              onClick={() => setAddOpen(true)}
            >
              + Add step
            </button>
          </div>

          {addOpen && (
            <AddStepForm
              groups={groups}
              keyDates={keyDates}
              emailLive={settings.emailCh && campaignEmailOn}
              smsLive={settings.smsCh && campaignSmsOn}
              campaignStart={campaignStart}
              campaignEnd={campaignEnd}
              onCancel={() => setAddOpen(false)}
              onSubmit={(draftStep) => {
                setAddOpen(false);
                onAddStep(draftStep);
              }}
            />
          )}

          {groups.map((group) => {
            const rows = visible.filter((t) => (t.grp ?? "main") === group.id);
            if (rows.length === 0) return null;
            const open = showAll[group.id];
            const shown = open ? rows : rows.slice(0, COLLAPSED_ROWS);
            return (
              <div className="esms-seq__group" key={group.id}>
                {group.name && (
                  <div className="esms-seq__group-head">
                    {group.name}
                    <span className="esms-seq__group-note">{group.note}</span>
                    <span className="esms-count">{rows.length}</span>
                  </div>
                )}
                <div className="esms-seq__table">
                  <TouchRow header dated rounded />
                  {shown.map((touch, index) => (
                    <div key={touch.id}>
                    <TouchRow
                      key={touch.id}
                      touch={toModel(touch, index)}
                      dated
                      onDays={(days) => onTouchChange(touch.id, { days })}
                      onDir={(dir: TriggerDirection) => onTouchChange(touch.id, { dir })}
                      onAnchor={(anchor) => onTouchChange(touch.id, { anchor })}
                      onToggle={() => onTouchChange(touch.id, { on: touch.on === false })}
                      onPreview={() => onPreviewTouch(touch.id)}
                      onMenu={(at) => setMenuAt({ id: touch.id, at })}
                      onCustomized={(at) => setCustAt({ id: touch.id, at })}
                      onBlockFix={() => {
                        const block = blockFor(touch, blockCtx);
                        if (block) onFixBlock(block.target);
                      }}
                      onReviewStatus={(next) => onTouchStatus(touch.id, next)}
                      reviewBusy={statusBusy?.has(touch.id)}
                    />
                    {/* The client's own words, inline. No hover: a reviewer wrote a
                        sentence about this message and hiding it is how it gets missed. */}
                    {touch.reviewStatus === "CHANGES_REQUESTED" && touch.reviewComment && (
                      <div className="touch-row__chg">
                        <span className="touch-row__chg-who">Client asked:</span>
                        <span className="touch-row__chg-note">“{touch.reviewComment}”</span>
                        <button
                          type="button"
                          className="touch-row__chg-edit"
                          onClick={() => onPreviewTouch(touch.id, "edit")}
                        >
                          Edit copy
                        </button>
                      </div>
                    )}
                    </div>
                  ))}
                  {rows.length > COLLAPSED_ROWS && (
                    <button
                      type="button"
                      className="esms-seq__more"
                      onClick={() => setShowAll((prev) => ({ ...prev, [group.id]: !open }))}
                    >
                      {open
                        ? "Show first 5"
                        : `Show all ${rows.length} touches (${rows.filter((r) => r.ch === "email").length} email, ${rows.filter((r) => r.ch === "sms").length} SMS)`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {menuAt && (() => {
        const touch = visible.find((t) => t.id === menuAt.id);
        if (!touch) return null;
        return (
          <TouchRowMenu
            at={menuAt.at}
            touch={touch}
            diff={diffOf(touch, originalOf(touch.id))}
            onPreview={() => onPreviewTouch(touch.id, "preview")}
            onEdit={() => onPreviewTouch(touch.id, "edit")}
            onSendTest={() => onSendTest(touch.id)}
            onReset={() => onResetTouch(touch.id)}
            onDelete={() => onDeleteTouch(touch.id)}
            onClose={() => setMenuAt(null)}
          />
        );
      })()}

      {custAt && (() => {
        const touch = visible.find((t) => t.id === custAt.id);
        if (!touch) return null;
        const diff = diffOf(touch, originalOf(touch.id));
        return (
          <CustomizedPopover
            at={custAt.at}
            touch={touch}
            original={originalOf(touch.id)}
            diff={diff}
            onReset={() => {
              onResetTouch(touch.id);
              setCustAt(null);
            }}
            onViewChanges={() => {
              setCustAt(null);
              onPreviewTouch(touch.id);
            }}
            onClose={() => setCustAt(null)}
          />
        );
      })()}
    </div>
  );
}

export default SequenceCard;
